// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
// Heavily inspired by https://www.npmjs.com/package/@azure/communication-react
import { MenuItem, Menu } from "@mui/material";
import { Button } from "../../Button/Button";
import { ChatMessageComponent } from "../ChatMessage/ChatMessageComponent";
import React, { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { aderantColors } from "../../Theme/aderantColors";
import "./Chat.css";
/**
 * Component displaying a chat.
 */
export function Chat(props) {
    const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentHoverMessageId, setCurrentHoverMessageId] = useState(undefined);
    const [currentMessageId, setCurrentMessageId] = useState(undefined);
    const [editMessageId, setEditMessageId] = useState("");
    const handleMessageHover = (event) => {
        const targetMessage = event.currentTarget;
        const isHovering = event.type === "mouseenter";
        if (!props.disableActions && isHovering) {
            setCurrentHoverMessageId(targetMessage.id);
        }
        else {
            setCurrentHoverMessageId(undefined);
        }
    };
    const actionMenuClickHandler = (event) => {
        const clickedElement = event.currentTarget;
        const messageElement = clickedElement.closest(".aderant-ChatMessage");
        if (messageElement) {
            setCurrentMessageId(messageElement.id);
            setAnchorEl(clickedElement);
            setIsActionsMenuOpen(!isActionsMenuOpen);
        }
    };
    const onUpdateMessageHandler = (messageId, content, selectedItems) => {
        handleClose();
        setEditMessageId("");
        if (props.onUpdateMessage) {
            props.onUpdateMessage(messageId, content, selectedItems);
        }
    };
    const onEditHandler = (e) => {
        const editMessageId = currentMessageId;
        handleClose();
        setEditMessageId(editMessageId || "");
        if (props.onEditMessage && editMessageId) {
            props.onEditMessage(editMessageId);
        }
    };
    const onCancelEditHandler = (messageId, originalContent, currentContent) => {
        handleClose();
        setEditMessageId("");
        if (props.onCancelEdit) {
            props.onCancelEdit(messageId, originalContent, currentContent);
        }
    };
    const onDeleteHandler = (e) => {
        const deleteMessageId = currentMessageId;
        handleClose();
        if (props.onDeleteMessage && deleteMessageId) {
            props.onDeleteMessage(deleteMessageId);
        }
    };
    const handleClose = () => {
        setIsActionsMenuOpen(false);
        setAnchorEl(null);
        setCurrentMessageId(undefined);
    };
    const defaultStyles = {
        container: {},
        actions: {
            menu: {},
            menuItem: {
                color: aderantColors.DarthVader
            },
            menuItemButton: {}
        }
    };
    const defaultChatRenderer = (props) => {
        const { id, userId, messages, onRenderMessage, disableActions, disableEditing, disableDelete, messagesBeingUpdated, showMessageDate, showMessageTime, styles, sendBoxPopupProps } = props;
        const combinedStyles = styles ? { ...defaultStyles, ...styles } : defaultStyles;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { id: id, "aria-label": "Chat", className: "aderant-Chat", style: combinedStyles.container },
                messages.map((message) => {
                    return (React.createElement(ChatMessageComponent, { key: message.messageId, message: message, userId: userId, onRenderMessage: onRenderMessage, disableActions: disableActions, showMessageDate: showMessageDate, showMessageTime: showMessageTime, styles: combinedStyles.message, onActionClick: actionMenuClickHandler, onUpdateMessage: onUpdateMessageHandler, onCancelEdit: onCancelEditHandler, handleMessageHover: handleMessageHover, isEditingMessage: editMessageId === message.messageId, isUpdatingMessage: messagesBeingUpdated?.includes(message.messageId), isActionMenuButtonVisible: (isActionsMenuOpen && currentMessageId === message.messageId) || (message.mine && currentHoverMessageId === message.messageId), sendBoxPopupProps: sendBoxPopupProps }));
                }),
                React.createElement(Menu, { className: "aderant-ChatMessage-actions-menu", open: isActionsMenuOpen, onClose: handleClose, anchorEl: anchorEl, "aria-label": "Chat Message Actions Menu", style: combinedStyles.actions.menu, anchorOrigin: {
                        vertical: "top",
                        horizontal: "left"
                    }, transformOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                    } },
                    React.createElement(MenuItem, { className: "aderant-ChatMessage-actions-edit", style: combinedStyles.actions.menuItem },
                        React.createElement(Button, { disabled: disableEditing ?? false, variant: "plain", size: "small", startIcon: React.createElement(EditIcon, null), "aria-label": "Chat Message Actions Edit", style: combinedStyles.actions.menuItemButton, onClick: onEditHandler, text: "Edit" })),
                    React.createElement(MenuItem, { className: "aderant-ChatMessage-actions-delete", style: combinedStyles.actions.menuItem },
                        React.createElement(Button, { disabled: disableDelete ?? false, variant: "plain", size: "small", startIcon: React.createElement(DeleteIcon, null), "aria-label": "Chat Message Actions Delete", style: combinedStyles.actions.menuItemButton, onClick: onDeleteHandler, text: "Delete" }))))));
    };
    const renderer = props.onRenderChat ?? defaultChatRenderer;
    return React.createElement(React.Fragment, null, renderer(props));
}
